import {
  ArrowLeft,
  CheckCircle2,
  Key,
  Loader2,
  Mail,
  MailCheck,
  Pencil,
  Plus,
  ShieldOff,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminUtenti,
  useInvitaUtente,
  useModificaUtente,
  useReinviaMagicLink,
  useRevocaUtente,
} from "@/hooks/useAdminUtenti";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { Parte, Ruolo, UtenteAutorizzato } from "@/types/api";

const PARTI: { value: Parte; label: string }[] = [
  { value: "maitime", label: "Maitime" },
  { value: "misterpet", label: "Mister Pet" },
];

const RUOLI: { value: Ruolo; label: string; parte: Parte }[] = [
  { value: "maitime_staff", label: "Maitime Staff", parte: "maitime" },
  { value: "mp_presidenza", label: "Presidenza", parte: "misterpet" },
  { value: "mp_management", label: "Management", parte: "misterpet" },
  { value: "mp_planning", label: "Planning", parte: "misterpet" },
  { value: "mp_logistica", label: "Logistica", parte: "misterpet" },
  { value: "mp_commerciale", label: "Commerciale", parte: "misterpet" },
  { value: "mp_viewer", label: "Viewer (sola lettura)", parte: "misterpet" },
];

function ruoloLabel(r: Ruolo): string {
  return RUOLI.find((x) => x.value === r)?.label ?? r;
}

function parteLabel(p: Parte): string {
  return PARTI.find((x) => x.value === p)?.label ?? p;
}

export function AdminUtentiPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: utenti = [], isLoading } = useAdminUtenti();
  const [invitaOpen, setInvitaOpen] = useState(false);
  const [modificaUtente, setModificaUtente] = useState<UtenteAutorizzato | null>(null);

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  const utentiOrdinati = [...utenti].sort((a, b) => {
    // attivi prima, poi per data invito desc
    if (a.attivo !== b.attivo) return a.attivo ? -1 : 1;
    return b.invitato_il.localeCompare(a.invitato_il);
  });

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Backoffice Maitime
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Utenti e accessi
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl pt-1">
            Allowlist degli utenti autorizzati al portale. Solo gli utenti in questa
            tabella e attivi possono fare login. Gli inviti generano un magic link
            via email (SendGrid).
          </p>
        </div>
        <Button onClick={() => setInvitaOpen(true)}>
          <Plus className="size-4" />
          Invita utente
        </Button>
      </header>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : utentiOrdinati.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-6 text-center text-sm text-muted-foreground">
          Nessun utente autorizzato. Invita il primo utente.
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs">
              <tr>
                <th className="text-left font-medium px-4 py-2">Email</th>
                <th className="text-left font-medium px-4 py-2">Nome</th>
                <th className="text-left font-medium px-4 py-2">Parte</th>
                <th className="text-left font-medium px-4 py-2">Ruolo</th>
                <th className="text-left font-medium px-4 py-2">Stato</th>
                <th className="text-left font-medium px-4 py-2">Ultimo accesso</th>
                <th className="text-right font-medium px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {utentiOrdinati.map((u) => (
                <UtenteRow
                  key={u.email}
                  utente={u}
                  onModifica={() => setModificaUtente(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InvitaDialog open={invitaOpen} onClose={() => setInvitaOpen(false)} />
      {modificaUtente && (
        <ModificaDialog
          utente={modificaUtente}
          onClose={() => setModificaUtente(null)}
        />
      )}
    </PageContainer>
  );
}

// ── Riga utente ──────────────────────────────────────────────────────

function UtenteRow({
  utente: u,
  onModifica,
}: {
  utente: UtenteAutorizzato;
  onModifica: () => void;
}) {
  const reinvia = useReinviaMagicLink();
  const revoca = useRevocaUtente();

  function handleReinvia() {
    if (!confirm(`Reinviare il magic link a ${u.email}?`)) return;
    reinvia.mutate(u.email);
  }

  function handleRevoca() {
    if (
      !confirm(
        `Revocare l'accesso a ${u.email}? Verrà rimosso dall'allowlist; potrai reinvitarlo in futuro.`
      )
    )
      return;
    revoca.mutate(u.email);
  }

  return (
    <tr className={cn(!u.attivo && "opacity-60")}>
      <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
      <td className="px-4 py-2">{u.nome_completo ?? "—"}</td>
      <td className="px-4 py-2">
        <Badge variant="outline" className="text-[10px]">
          {parteLabel(u.parte)}
        </Badge>
      </td>
      <td className="px-4 py-2 text-xs">{ruoloLabel(u.ruolo)}</td>
      <td className="px-4 py-2">
        {u.attivo ? (
          <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
            attivo
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
            disattivato
          </Badge>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-muted-foreground">
        {u.ultimo_accesso_il
          ? formatDataOra(u.ultimo_accesso_il)
          : u.primo_accesso_il
            ? "mai dopo il primo"
            : "mai entrato"}
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            asChild
            title="Permessi personali (override)"
          >
            <Link to={`/admin/utenti/${encodeURIComponent(u.email)}/permessi`}>
              <Key className="size-3.5" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReinvia}
            disabled={!u.attivo || reinvia.isPending}
            title="Reinvia magic link via email"
          >
            {reinvia.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <MailCheck className="size-3.5" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onModifica} title="Modifica">
            <Pencil className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRevoca}
            disabled={revoca.isPending}
            title="Revoca accesso"
            className="text-destructive hover:text-destructive"
          >
            {revoca.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <ShieldOff className="size-3.5" />
            )}
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── Dialog invita ────────────────────────────────────────────────────

function InvitaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [parte, setParte] = useState<Parte>("misterpet");
  const [ruolo, setRuolo] = useState<Ruolo>("mp_viewer");
  const [nome, setNome] = useState("");
  const [note, setNote] = useState("");
  const [inviaMail, setInviaMail] = useState(true);
  const mutation = useInvitaUtente();

  function reset() {
    setEmail("");
    setParte("misterpet");
    setRuolo("mp_viewer");
    setNome("");
    setNote("");
    setInviaMail(true);
    mutation.reset();
  }

  function handleParteChange(p: Parte) {
    setParte(p);
    // Auto-allinea ruolo al primo valido per la parte
    const primoRuolo = RUOLI.find((r) => r.parte === p);
    if (primoRuolo && !RUOLI.find((r) => r.value === ruolo && r.parte === p)) {
      setRuolo(primoRuolo.value);
    }
  }

  function salva() {
    if (!email.trim()) return;
    mutation.mutate(
      {
        email: email.trim().toLowerCase(),
        parte,
        ruolo,
        nome_completo: nome.trim() || null,
        note: note.trim() || null,
        invia_magic_link: inviaMail,
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
      }
    );
  }

  const ruoliPerParte = RUOLI.filter((r) => r.parte === parte);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Invita utente</DialogTitle>
          <DialogDescription className="text-xs">
            Aggiungi una nuova email all'allowlist. Se attivi l'invio mail,
            l'utente riceverà un magic link da SendGrid pronto per il login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <FormField label="Email *">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome.cognome@dominio.it"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
              autoFocus
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Parte">
              <select
                value={parte}
                onChange={(e) => handleParteChange(e.target.value as Parte)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {PARTI.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Ruolo">
              <select
                value={ruolo}
                onChange={(e) => setRuolo(e.target.value as Ruolo)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {ruoliPerParte.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <FormField label="Nome (opzionale)">
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Es. Mario Rossi"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </FormField>
          <FormField label="Note (opzionale)">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Es. Direttore vendite, primo punto di contatto"
            />
          </FormField>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={inviaMail}
              onChange={(e) => setInviaMail(e.target.checked)}
            />
            <Mail className="size-3.5 text-muted-foreground" />
            Invia magic link via email subito
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button onClick={salva} disabled={mutation.isPending || !email.trim()}>
            {mutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Invita
          </Button>
        </DialogFooter>
        {mutation.isError && (
          <p className="text-xs text-destructive">
            Errore: {(mutation.error as Error).message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog modifica ──────────────────────────────────────────────────

function ModificaDialog({
  utente,
  onClose,
}: {
  utente: UtenteAutorizzato;
  onClose: () => void;
}) {
  const [parte, setParte] = useState<Parte>(utente.parte);
  const [ruolo, setRuolo] = useState<Ruolo>(utente.ruolo);
  const [nome, setNome] = useState(utente.nome_completo ?? "");
  const [note, setNote] = useState(utente.note ?? "");
  const [attivo, setAttivo] = useState(utente.attivo);
  const mutation = useModificaUtente();

  function handleParteChange(p: Parte) {
    setParte(p);
    if (!RUOLI.find((r) => r.value === ruolo && r.parte === p)) {
      const primo = RUOLI.find((r) => r.parte === p);
      if (primo) setRuolo(primo.value);
    }
  }

  function salva() {
    mutation.mutate(
      {
        email: utente.email,
        body: {
          parte,
          ruolo,
          nome_completo: nome.trim() || null,
          note: note.trim() || null,
          attivo,
        },
      },
      { onSuccess: onClose }
    );
  }

  const ruoliPerParte = RUOLI.filter((r) => r.parte === parte);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Modifica utente</DialogTitle>
          <DialogDescription className="text-xs">
            <span className="font-mono">{utente.email}</span> · L'email è la chiave e
            non può essere cambiata. Per cambiare email: revoca + reinvita.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Parte">
              <select
                value={parte}
                onChange={(e) => handleParteChange(e.target.value as Parte)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {PARTI.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Ruolo">
              <select
                value={ruolo}
                onChange={(e) => setRuolo(e.target.value as Ruolo)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {ruoliPerParte.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <FormField label="Nome">
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </FormField>
          <FormField label="Note">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </FormField>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={attivo}
              onChange={(e) => setAttivo(e.target.checked)}
            />
            {attivo ? (
              <CheckCircle2 className="size-3.5 text-success" />
            ) : (
              <XCircle className="size-3.5 text-muted-foreground" />
            )}
            Accesso attivo
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button onClick={salva} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Salva
          </Button>
        </DialogFooter>
        {mutation.isError && (
          <p className="text-xs text-destructive">
            Errore: {(mutation.error as Error).message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
